import type { Citation } from '../../core/contracts/index.js';

export interface RagDocument {
  id: string;
  tenantId: string;
  title: string;
  content: string;
  tags?: string[];
  version?: string;
  createdAt: number;
}

export interface RagChunk {
  id: string;
  documentId: string;
  tenantId: string;
  title: string;
  text: string;
  embedding: number[];
  version: string;
}

export class RagIndexer {
  private chunks: RagChunk[] = [];

  indexDocument(doc: RagDocument): RagChunk[] {
    const rawChunks = this.splitText(doc.content, 200);
    const newChunks: RagChunk[] = rawChunks.map((text, idx) => ({
      id: `${doc.id}-chunk-${idx}`,
      documentId: doc.id,
      tenantId: doc.tenantId,
      title: doc.title,
      text,
      embedding: this.generateSimpleEmbedding(text),
      version: doc.version || '1.0.0',
    }));

    // Replace old chunks for document if re-indexing
    this.chunks = this.chunks.filter((c) => c.documentId !== doc.id);
    this.chunks.push(...newChunks);
    return newChunks;
  }

  removeDocument(documentId: string): void {
    this.chunks = this.chunks.filter((c) => c.documentId !== documentId);
  }

  search(query: string, tenantId: string, limit: number = 5): Citation[] {
    const queryEmb = this.generateSimpleEmbedding(query);
    const tenantChunks = this.chunks.filter((c) => c.tenantId === tenantId);

    const scored = tenantChunks.map((chunk) => ({
      chunk,
      score: this.cosineSimilarity(queryEmb, chunk.embedding),
    }));

    scored.sort((a, b) => b.score - a.score);

    return scored.slice(0, limit).map((s) => ({
      sourceId: s.chunk.documentId,
      title: s.chunk.title,
      snippet: s.chunk.text,
      score: Math.round(s.score * 100) / 100,
      version: s.chunk.version,
    }));
  }

  private splitText(text: string, maxLen: number): string[] {
    const words = text.split(/\s+/);
    const chunks: string[] = [];
    let current: string[] = [];

    for (const w of words) {
      current.push(w);
      if (current.join(' ').length >= maxLen) {
        chunks.push(current.join(' '));
        current = [];
      }
    }
    if (current.length > 0) {
      chunks.push(current.join(' '));
    }
    return chunks;
  }

  private generateSimpleEmbedding(text: string): number[] {
    const vec = new Array(16).fill(0);
    for (let i = 0; i < text.length; i++) {
      const code = text.charCodeAt(i);
      vec[i % 16] += code / 255;
    }
    return vec;
  }

  private cosineSimilarity(a: number[], b: number[]): number {
    let dot = 0, normA = 0, normB = 0;
    for (let i = 0; i < a.length; i++) {
      dot += a[i] * b[i];
      normA += a[i] * a[i];
      normB += b[i] * b[i];
    }
    if (normA === 0 || normB === 0) return 0;
    return dot / (Math.sqrt(normA) * Math.sqrt(normB));
  }
}
